import React from "react";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridFilterChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { FiUser } from "react-icons/fi";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";

import { Field } from "@progress/kendo-react-form";
import FormSelectionField from "../../../components/formFields/FormSelectionField";

const UserIcon = FiUser as any;

const DATA_ITEM_KEY = "ID";
const SELECTED_FIELD = "selected";

interface ContactSelectionSectionProps {
  contactList: any[];
  filteredContactList: any[];
  pagedContactList: any[];
  selectedGridRows: { [key: string]: boolean | number[] };
  filter: CompositeFilterDescriptor | undefined;
  page: { skip: number; take: number };
  onFilterChange: (e: GridFilterChangeEvent) => void;
  onPageChange: (e: GridPageChangeEvent) => void;
  onSelectionChange: (e: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (e: GridHeaderSelectionChangeEvent) => void;
  onToggleSelectAllFiltered?: () => void;
  stateOptions: { value: number; label: string }[];
  selectedStateID?: number | null;
  onStateChange: (event: any) => void;
}

export const ContactSelectionSection: React.FC<
  ContactSelectionSectionProps
> = ({
  filteredContactList,
  pagedContactList,
  selectedGridRows,
  filter,
  page,
  onFilterChange,
  onPageChange,
  onSelectionChange,
  onHeaderSelectionChange,
  stateOptions,
  onStateChange,
}) => {
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const selectedInFiltered = filteredContactList.filter(
    (item: any) => selectedGridRows[item.ID] === true,
  ).length;

  return (
    <ShadowCard
      style={{
        padding: "16px",
        borderRadius: "16px",
        border: "1px solid #cdddf1ff",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {/* Single Top Toolbar: Title + Selected Count Badge + State Filter Dropdown + Select All Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          paddingBottom: "8px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        {/* Left Side: Title & Selected Count Pill */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              padding: "6px",
              borderRadius: "8px",
              backgroundColor: "#eff6ff",
              color: "#174583",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconComponent icon={UserIcon} style={{ fontSize: "16px" }} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}
            >
              Contacts ({filteredContactList.length})
            </div>

            <span
              style={{
                backgroundColor: selectedInFiltered > 0 ? "#dcfce7" : "#f1f5f9",
                color: selectedInFiltered > 0 ? "#15803d" : "#64748b",
                border: `1px solid ${selectedInFiltered > 0 ? "#bbf7d0" : "#e2e8f0"}`,
                padding: "2px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              Selected: {selectedInFiltered}
            </span>
          </div>
        </div>

        {/* Right Side: State Filter Dropdown */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{ width: "220px", display: "flex", alignItems: "center" }}
          >
            <Field
              name="StateID"
              placeholder="Filter by State..."
              component={FormSelectionField}
              options={stateOptions}
              onChange={onStateChange}
              size="small"
              wrapperStyle={{ margin: 0, width: "100%" }}
            />
          </div>
        </div>
      </div>

      {/* Kendo Grid */}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 220px)", minHeight: "400px" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        onFilterChange={onFilterChange}
        data={pagedContactList.map((item) => ({
          ...item,
          [SELECTED_FIELD]: selectedGridRows[item.ID] === true,
        }))}
        total={filteredContactList.length}
        skip={page.skip}
        take={page.take}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 50, 100, 500],
          pageSizeValue: page.take,
          type: "input",
        }}
        onPageChange={onPageChange}
        dataItemKey={DATA_ITEM_KEY}
        selectedField={SELECTED_FIELD}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
        reorderable={true}
      >
        <Column
          filterable={false}
          field={SELECTED_FIELD}
          width={50}
          headerSelectionValue={
            pagedContactList.length > 0 &&
            pagedContactList.every((item) => selectedGridRows[item.ID] === true)
          }
        />

        <Column
          field="FullName"
          title="Contact Name"
          width={240}
          cells={{
            data: (props: any) => {
              const name =
                props.dataItem.FullName ||
                `${props.dataItem.FirstName || ""} ${props.dataItem.LastName || ""}`.trim() ||
                "N/A";
              return (
                <td {...props.tdProps} className="table-cell">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#1e293b" }}>
                      {name}
                    </span>
                  </div>
                </td>
              );
            },
          }}
        />

        <Column
          field="EmailID"
          title="Email Address"
          width={280}
          cells={{
            data: (props: any) => {
              const email = props.dataItem.EmailID;
              return (
                <td {...props.tdProps} className="table-cell">
                  {email ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          color: "#2563eb",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {email}
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                      -
                    </span>
                  )}
                </td>
              );
            },
          }}
        />

        <Column field="MobileNo" title="Mobile Number" width={150} />

        <Column
          field="ReferalCode"
          title="Referral Code"
          width={140}
          cells={{
            data: (props: any) => {
              const code = props.dataItem.ReferalCode;
              return (
                <td {...props.tdProps} className="table-cell">
                  {code ? (
                    <span
                      style={{
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontFamily: "monospace",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {code}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
              );
            },
          }}
        />

        <Column field="CityName" title="City" width={140} />
      </KendoGrid>
    </ShadowCard>
  );
};
